import type { TravelPlans as MatterTravelPlans } from '~/gql/graphql';

export enum UserRole {
  ATTORNEY = 'Attorney',
  EXPERT = 'Expert',
  ADMIN = 'Admin',
}

export enum MatterStatus {
  Active = 'Active',
  Pending = 'Pending',
  Closed = 'Closed',
}

export enum WrittenReportNeeded {
  Yes = 'Yes',
  No = 'No',
  Maybe = 'Maybe',
}

export type MatterSide = 'Plaintiff' | 'Defense';

export const MATTER_SIDE_OPTIONS: readonly MatterSide[] = [
  'Plaintiff',
  'Defense',
] as const;

export enum TravelPlans {
  None = 'None',
  Local = 'Local',
  Regional = 'Regional',
  National = 'National',
  International = 'International',
}

export type MattersTabValue = 'active' | 'inactive' | 'closed';

export type RetainerRequestStatus = 'Pending' | 'Approved' | 'Declined';

export enum ExpertDeclineMatterReason {
  DoesNotFitExpertise = "Doesn't fit my expertise",
  ScheduleConflict = 'Schedule conflict',
  ConflictOfInterest = 'Conflict of interest',
  OutsideSpecialty = 'Case outside my specialty',
  Other = 'Other',
}

export enum BillingEntryType {
  RetainerPurchase = 'RetainerPurchase',
  HoursLogged = 'HoursLogged',
  TimeLogged = 'TimeLogged',
}

export type TravelPreference = 'Local only' | 'Regional' | 'National';

export interface CertificateLicenseRow {
  name: string;
  issuingBody: string;
  date: string;
}

export interface ProfessionalExperienceRow {
  role: string;
  organization: string;
  period: string;
  description?: string;
}

export interface PublicationRow {
  title: string;
  journal: string;
  year?: string;
  authors?: string;
}

export interface User {
  id: string;
  name: string;
  role: UserRole;
  email: string;
  phone?: string;
  avatarUrl?: string;
  firm?: string;
  credentials?: string;
  hourlyRate?: number;
  /** Expert profile rates from `me` ($/hr); used for retainer request modal. */
  depositionHourlyRate?: number;
  examinationHourlyRate?: number;
  recordsReviewHourlyRate?: number;
  testimonyHourlyRate?: number;
  specialty?: string;
  location?: string;
  travelPreference?: TravelPreference;

  industry?: string;

  statesOfPractice?: string;

  yearsExperience?: string;

  subspecialty?: string;

  previouslyTestified?: boolean;

  certifications?: string[];

  linkedInUrl?: string;

  cvUrl?: string;

  retainerRequired?: string;

  imeRate?: number;

  /** From `me.expert` when role is Expert; null until first Connect account exists. */
  stripeConnectAccountId?: string | null;
  /** From `me.expert.stripeConnectOnboarded` when role is Expert. */
  stripeConnectOnboarded?: boolean;
  certificatesLicenses?: CertificateLicenseRow[];
  professionalExperience?: ProfessionalExperienceRow[];
  publications?: PublicationRow[];
  professionalSummary?: string;
}

export interface Attorney extends User {
  firm?: string;
}

export interface Expert extends User {
  phone?: string;
  credentials?: string;
  hourlyRate?: number;
  specialty?: string;
  location?: string;
  travelPreference?: TravelPreference;
  cvUrl?: string;
}

export interface Matter {
  id: string;
  title: string;
  description: string;
  caseNumber?: string;
  court?: string;
  location?: string;
  keyDates?: string;

  nextDeadline?: string;
  status: MatterStatus;
  attorneyId: string;
  /** Hired expert IDs on this matter (attorney can invite multiple, then hire; each hired expert can have retainers). */
  expertIds?: string[];

  invitedExpertIds?: string[];
  travelPlans?: MatterTravelPlans;
  writtenReportNeeded?: WrittenReportNeeded;
  side?: MatterSide;
  expeditedReviewRequired?: boolean;
  expeditedReviewTimeframe?: string;
  createdAt: string;
  updatedAt: string;
  /** Set when status is Closed. */
  closedAt?: string;
}

export interface Message {
  id: string;
  matterId: string;
  senderId: string;
  senderEmail?: string;
  senderName?: string;
  senderAvatarUrl?: string;
  content: string;
  createdAt: string;
  readAt?: string;
}

export interface RetainerRequest {
  id: string;
  matterId: string;
  expertId: string;
  title?: string;
  hours: number;
  description: string;
  subtotal: number;
  platformFee: number;
  total: number;
  status: RetainerRequestStatus;
  createdAt: string;
  declinedMessage?: string;
  expertSignedAt?: string;
  attorneySignedAt?: string;
  initiatedBy?: 'attorney' | 'expert';
  billingType?: 'hourly' | 'flat_rate';
  rateKey?: string;
  customServiceLabel?: string;
  completedAt?: string;
  flatAmountPurchased?: number;
  /** Optional note from expert to attorney when requesting the retainer. */
  additionalDetails?: string;
}

export interface RetainerBalance {
  matterId: string;
  hoursRemaining: number;
  hoursPurchased: number;
  hoursUsed: number;
  flatAmountPurchased?: number;
}

export interface BillingEntry {
  id: string;
  matterId: string;
  type: BillingEntryType;
  description: string;
  hours: number;
  amount: number;
  balance: number;
  date: string;
  retainerRequestId?: string;
  expertId?: string;
  invoiceUrl?: string;
}
